import {decorate, computed} from 'mobx';
import {theme} from '@octane/spark';
import {BaseStore} from "./base-store";
import {BRP_FINANCIAL_IDENTIFIER, BUFFALO_IDENTIFIER, isBrpFinancialIdentifier} from "../utils/brp-financial";


const BRP_FINANCIAL_THEME_KEY = BRP_FINANCIAL_IDENTIFIER;
const BUFFALO_THEME_KEY = BUFFALO_IDENTIFIER;
const DEFAULT_SPARK_THEME = {
    ...theme,
    colors: {
        ...theme.colors,
        brand: {
            ...theme.colors.brand,
        },
        monochrome: {
            ...theme.colors.monochrome,
        },
    },
};

export class ThemeStore extends BaseStore {
    // themes
    static THEME_DEFAULT = null;
    static THEME_BLACK = Object.freeze({
        key: "black",
        md5_hash: "3a49337c5724d18079602a4ca5a91626",
        color: "#111111"
    });
    static THEME_POLARIS_BLUE = Object.freeze({
        key: "polaris_blue",
        md5_hash: "8a6c53818dc819ec971b6031d49e9373",
        color: "#004e97"
    });
    static THEME_SLINGSHOT_BLACK = Object.freeze({
        key: "slingshot_black",
        md5_hash: "c9d8f0a1ac49790e7e87d9e3fee85877",
        color: "#000000"
    });
    static THEME_INDIAN_RED = Object.freeze({
        key: "indian_red",
        md5_hash: "8e150fc012f9d2eb7b61e16abca9462a",
        color: "#862633"
    });
    static THEME_CAMPING_WORLD_BLUE = Object.freeze({
        key: "camping_world_blue",
        md5_hash: "70ef967be58c3d1c5981f43a42c09037",
        color: "#00669E"
    });
    static THEME_AAAF_BLUE = Object.freeze({
        key: "aaaf_blue",
        md5_hash: "84ee020d04c1bba0729c00e3ec17c0ec",
        color: "#1F368B"
    });
    static THEME_BRP_FINANCIAL = Object.freeze({
        key: BRP_FINANCIAL_THEME_KEY,
        md5_hash: "6273010ce8147f4f292a0aebc2db67b3",
        color: "#000000",
        alternatePrimaryColor: "#FFD200",
        alternatePrimaryFontColor: "#000000",
    });
    static THEME_BUFFALO = Object.freeze({
        ...ThemeStore.THEME_BRP_FINANCIAL,
        key: BUFFALO_THEME_KEY,
    });

    /**
     * Mapping of partner identifiers to themes, used when appTheme is absent from the URL
     * (e.g. paperwork deep links that don't carry the hash query param)
     */
    static PARTNER_IDENTIFIER_THEME_MAPPING = Object.freeze({
        'jAwNlR4': ThemeStore.THEME_AAAF_BLUE,  // DriversMart
        'oALnnkX': ThemeStore.THEME_BUFFALO,    // Buffalo
        [BRP_FINANCIAL_IDENTIFIER]: ThemeStore.THEME_BUFFALO,
        [BUFFALO_IDENTIFIER]: ThemeStore.THEME_BUFFALO,
    });

    /**
     * Mapping of all accepted theme hashes to our themes
     */
    static THEME_HASH_APP_THEME_MAPPING = Object.freeze({
        [ThemeStore.THEME_BLACK.md5_hash]: ThemeStore.THEME_BLACK,
        [ThemeStore.THEME_POLARIS_BLUE.md5_hash]: ThemeStore.THEME_POLARIS_BLUE, // reveo
        [ThemeStore.THEME_SLINGSHOT_BLACK.md5_hash]: ThemeStore.THEME_SLINGSHOT_BLACK, // slingshot
        [ThemeStore.THEME_INDIAN_RED.md5_hash]: ThemeStore.THEME_INDIAN_RED, // indian motorcycles
        [ThemeStore.THEME_CAMPING_WORLD_BLUE.md5_hash]: ThemeStore.THEME_CAMPING_WORLD_BLUE, // camping world (RV.com)
        [ThemeStore.THEME_AAAF_BLUE.md5_hash]: ThemeStore.THEME_AAAF_BLUE,
        [ThemeStore.THEME_BUFFALO.md5_hash]: ThemeStore.THEME_BUFFALO,

        // Note: deprecated theme hashes (still used by some experiences, but should not be reused)
        "c21f969b5f03d33d43e04f8f136e7682": ThemeStore.THEME_DEFAULT,  // default
        "6f81a9dd183438cecec18c0dc1aa2861": ThemeStore.THEME_DEFAULT,  // brp
        "1b5d3e832d157714b8fe8d2cd64ed3e7": ThemeStore.THEME_BLACK, // cycle world
        "84ee020d04c1bba0729c00e3ec17c0ec": ThemeStore.THEME_AAAF_BLUE,
    });

    /**
     * The theme object applied to the experience
     */
    get theme() {
        // url.parse returns an array when the param appears more than once; normalise to a string
        const rawThemeHash = this.historyStore.queryParams.appTheme;
        // Normalize the theme hash to handle cases where the URL contains
        // multiple appTheme params (e.g. ?appTheme=hash1&appTheme=hash2)
        const themeHash = Array.isArray(rawThemeHash) ? rawThemeHash[0] : rawThemeHash;
        const partnerIdentifier = this.partnerStore.partnerIdentifier;
        const partnerTheme = this.constructor.PARTNER_IDENTIFIER_THEME_MAPPING[partnerIdentifier] || null;
        if (themeHash) {
            return this.constructor.THEME_HASH_APP_THEME_MAPPING[themeHash] || partnerTheme;
        }

        // Fall back to partner-identifier mapping for deep links that don't carry appTheme
        // (e.g. paperwork URLs, DriversMart — BLUR-1094)
        return partnerTheme;
    }

    /**
     * The class applied to the html tag
     */
    get htmlClass() {
        if (this.theme) {
            return `html_${this.theme.md5_hash}`;
        }
        return "";
    }
    /**
     * Used to determine if reveo marketplace in the ReveoCTAFooter
     */
    get isReveoMarketplace() {
        return !!this.theme && this.theme.key === 'polaris_blue';
    }

    get isBrpFinancialTheme() {
        return !!this.theme && isBrpFinancialIdentifier(this.theme.key);
    }

    get primaryColor() {
        return this.theme?.color ?? null;
    }

    get alternatePrimaryColor() {
        if(this.isBrpFinancialTheme) {
            return this.theme?.alternatePrimaryColor ?? null;
        }
        return null;
    }

    get alternatePrimaryFontColor() {
        if(this.isBrpFinancialTheme) {
            return this.theme?.alternatePrimaryFontColor ?? null;
        }
        return null;
    }

    get serializedTheme() {
        if (!this.theme) return null;
        const {key, md5_hash, color} = this.theme;
        return {key, md5_hash, color};
    }

    /**
     * Cached pre-computation of both theme variants (default + alternate).
     * @computed ensures a stable object reference is returned until dependencies change,
     * preventing unnecessary ThemeProvider re-renders.
     */
    get _sparkThemes() {
        if (!this.theme) return null;

        // Inlined to avoid @computed indirection within this @computed getter
        const isBrpFinancial = !!(this.theme && isBrpFinancialIdentifier(this.theme.key));

        // Use spread — never mutate the imported theme singleton
        const defaultTheme = {
            ...theme,
            colors: {
                ...theme.colors,
                brand: {
                    ...theme.colors.brand,
                    rideOctaneBlue: this.theme.color,
                }
            },
        };

        if (!isBrpFinancial) {
            return { default: defaultTheme, alternate: defaultTheme };
        }

        const alternateTheme = {
            ...defaultTheme,
            colors: {
                ...defaultTheme.colors,
                brand: {
                    ...defaultTheme.colors.brand,
                    rideOctaneBlue: this.theme.alternatePrimaryColor,
                },
            },
        };

        return { default: defaultTheme, alternate: alternateTheme };
    }

    buildSparkTheme(useAlternatePrimaryColor = false) {
        const themes = this._sparkThemes;
        if (!themes) return DEFAULT_SPARK_THEME;
        return this.isBrpFinancialTheme && useAlternatePrimaryColor ? themes.alternate : themes.default;
    }

}

decorate(ThemeStore, {
    theme: computed,
    htmlClass: computed,
    isReveoMarketplace: computed,
    isBrpFinancialTheme: computed,
    primaryColor: computed,
    alternatePrimaryColor: computed,
    alternatePrimaryFontColor: computed,
    serializedTheme: computed,
    _sparkThemes: computed,
});
